import { MeasurementAnnotation, ScaleInfo } from "custom-models/ViewerTypes";

import { AnnotationColors } from "./subtree";
import ViewerFacade from "../ViewerFacade";

const EXPECTED_TEXT_WIDTH = 130;
const EXPECTED_TEXT_HEIGHT = 20;

const FONT_SIZE = 14;

interface MeasurementProps {
  annotation: MeasurementAnnotation;
  scaleInfo: ScaleInfo;
  viewerFacade: ViewerFacade;
  isSelected?: boolean;
}

const Measurement = (props: MeasurementProps) => {
  const { point1, point2, label } = props.annotation;
  const color = props.isSelected
    ? AnnotationColors.SELECTED
    : props.annotation.id
    ? AnnotationColors.DEFAULT
    : AnnotationColors.ACTIVE;
  const scaleInfo = props.scaleInfo;
  const scale = scaleInfo.scaleX;

  if (!point1 || !point2) {
    return <></>;
  }

  if (point1.x === point2.x && point1.y === point2.y) {
    return (
      <>
        <circle
          key="circle-1"
          cx={point1.x}
          cy={point2.y}
          r={5 / scale}
          fill={color}
        />
        <line
          key="line-vert-1"
          x1={point1.x}
          y1={point1.y - 9 / scale}
          x2={point1.x}
          y2={point1.y + 9 / scale}
          strokeWidth={2.0 / scale}
          stroke={color}
        />
        <line
          key="line-horiz-1"
          x1={point1.x - 9 / scale}
          y1={point1.y}
          x2={point1.x + 9 / scale}
          y2={point1.y}
          strokeWidth={2.0 / scale}
          stroke={color}
        />
      </>
    );
  }

  // Figure out where the label goes
  const r = 20; // image coords... distance from center
  const centerX = (point1.x + point2.x) / 2;
  const centerY = (point1.y + point2.y) / 2;
  const dist = Math.sqrt(
    (point2.x - point1.x) * (point2.x - point1.x) +
      (point2.y - point1.y) * (point2.y - point1.y)
  );
  // Unit vector from point1 to point2:
  const unitX = (point2.x - point1.x) / dist;
  const unitY = (point2.y - point1.y) / dist;
  // Rotate unit vector CCW by 90 degrees:
  let rotX = -unitY;
  let rotY = unitX;
  if (point2.y > point1.y) {
    // CW will look better if line is pointing up
    rotX = -rotX;
    rotY = -rotY;
  }
  let labelX = centerX + r * rotX;
  let labelY = centerY + r * rotY;
  let fontsize = Math.floor(FONT_SIZE / scale);
  let textAnchor = "start";
  const textLength = EXPECTED_TEXT_WIDTH / scale;
  const textHeight = EXPECTED_TEXT_HEIGHT / scale;
  if (scaleInfo.width - labelX < textLength) {
    // Label is running off the right edge; move label to the other side of the line
    textAnchor = "end";
    labelX = centerX - r * rotX;
    labelY = centerY - r * rotY;
  }
  return (
    <>
      <circle
        key="circle-1"
        cx={point1.x}
        cy={point1.y}
        r={5 / scale}
        fill={color}
      />
      <line
        key="line-vert-1"
        x1={point1.x}
        y1={point1.y - 9 / scale}
        x2={point1.x}
        y2={point1.y + 9 / scale}
        strokeWidth={2.0 / scale}
        stroke={color}
      />
      <line
        key="line-horiz-1"
        x1={point1.x - 9 / scale}
        y1={point1.y}
        x2={point1.x + 9 / scale}
        y2={point1.y}
        strokeWidth={2.0 / scale}
        stroke={color}
      />
      <circle
        key="circle-2"
        cx={point2.x}
        cy={point2.y}
        r={5 / scale}
        fill={color}
      />
      <line
        key={"line-vert-2"}
        x1={point2.x}
        y1={point2.y - 9 / scale}
        x2={point2.x}
        y2={point2.y + 9 / scale}
        strokeWidth={2.0 / scale}
        stroke={color}
      />
      <line
        key="line-horiz-2"
        x1={point2.x - 9 / scale}
        y1={point2.y}
        x2={point2.x + 9 / scale}
        y2={point2.y}
        strokeWidth={2.0 / scale}
        stroke={color}
      />
      <line
        key="line-connector"
        x1={point1.x}
        y1={point1.y}
        x2={point2.x}
        y2={point2.y}
        strokeWidth={3.0 / scale}
        stroke={color}
      />
      {label && (
        <>
          <rect
            key="label-rect"
            x={textAnchor === "end" ? labelX - textLength : labelX}
            y={labelY - textHeight * 0.8}
            width={textLength}
            height={textHeight}
            fill="rgba(240, 240, 240, 0.85)"
            transform={`rotate(${-scaleInfo.rotation} ${labelX} ${labelY})`}
          />
          <text
            key="label-text"
            x={labelX}
            y={labelY}
            textAnchor={textAnchor}
            style={{ fontSize: `${fontsize}pt` }}
            fill={color}
            textLength={textLength}
            transform={`rotate(${-scaleInfo.rotation} ${labelX} ${labelY})`}
          >
            {label}
          </text>
        </>
      )}
    </>
  );
};
export default Measurement;
